import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { cn } from "@/lib/utils";
import { getAssistantErrorMessage } from "../lib/ai-assistant-errors";
import type { AssistantOperationApprovalTimelineItem } from "../types";
import { AssistantLinkedText } from "./assistant-linked-text";

function getBadgeClassName(tone: AssistantOperationApprovalTimelineItem["badgeTone"]) {
  switch (tone) {
    case "success":
      return "bg-emerald-50 text-emerald-700";
    case "warning":
      return "bg-amber-50 text-amber-700";
    case "destructive":
      return "bg-red-50 text-red-600";
    default:
      return "bg-blue-50 text-blue-700";
  }
}

/**
 * The gate itself, without the timeline placement around it. An activity turn shows the same card
 * for an approval that never became a row of its own, so the card asks for what it renders and
 * acts on — never for `key`/`order`/`stepOrder`, which only a projected row has.
 */
export type AssistantOperationApprovalGate = Pick<
  AssistantOperationApprovalTimelineItem,
  | "runId"
  | "threadId"
  | "title"
  | "titleParts"
  | "description"
  | "descriptionParts"
  | "badgeLabel"
  | "badgeTone"
  | "primaryActionLabel"
  | "secondaryActionLabel"
  | "assistantToolCallId"
  | "assistantToolCallIds"
  | "toolCallSummaries"
  | "toolCallSummaryParts"
>;

interface AssistantOperationApprovalCardProps {
  projectId: Id<"projects">;
  item: AssistantOperationApprovalGate;
  compact?: boolean;
}

export function AssistantOperationApprovalCard({
  projectId,
  item,
  compact = false,
}: AssistantOperationApprovalCardProps) {
  const approveToolCall = useObservedMutation(api.claudia.approveToolCall, {
    feature: "assistant",
    operation: "approve-tool-call",
    includeRequestId: true,
  });
  const denyToolCall = useObservedMutation(api.claudia.denyToolCall, {
    feature: "assistant",
    operation: "deny-tool-call",
    includeRequestId: true,
  });
  const abortRun = useObservedMutation(api.claudia.abortRun, {
    feature: "assistant",
    operation: "abort-run",
    includeRequestId: true,
  });
  const [inFlightAction, setInFlightAction] = useState<"abort" | "approve" | "deny" | null>(null);
  const representativeToolCallId = item.assistantToolCallIds?.[0] ?? item.assistantToolCallId;

  const handleApprove = async () => {
    if (inFlightAction || !representativeToolCallId) {
      return;
    }

    setInFlightAction("approve");
    try {
      await approveToolCall({
        projectId,
        threadId: item.threadId,
        runId: item.runId,
        assistantToolCallId: representativeToolCallId,
      });
    } catch (cause: unknown) {
      toast.error(getAssistantErrorMessage(cause, "Failed to approve request"));
    } finally {
      setInFlightAction(null);
    }
  };

  const handleDeny = async () => {
    if (inFlightAction || !representativeToolCallId) {
      return;
    }

    setInFlightAction("deny");
    try {
      await denyToolCall({
        projectId,
        threadId: item.threadId,
        runId: item.runId,
        assistantToolCallId: representativeToolCallId,
      });
    } catch (cause: unknown) {
      toast.error(getAssistantErrorMessage(cause, "Failed to deny request"));
    } finally {
      setInFlightAction(null);
    }
  };

  const handleAbort = async () => {
    if (inFlightAction) {
      return;
    }

    setInFlightAction("abort");
    try {
      await abortRun({
        projectId,
        threadId: item.threadId,
        runId: item.runId,
      });
    } catch (cause: unknown) {
      toast.error(getAssistantErrorMessage(cause, "Failed to abort run"));
    } finally {
      setInFlightAction(null);
    }
  };

  return (
    <section
      aria-label={`Operation approval: ${item.badgeLabel}`}
      data-testid="assistant-operation-approval-card"
      className={cn(
        "border border-border/70 bg-card px-4 py-3",
        compact ? "rounded-md" : "rounded-lg"
      )}
    >
      <div className={cn("flex items-start gap-3", compact ? "flex-col" : "justify-between")}>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-start gap-2">
            <span className="shrink-0 text-[11px] font-medium uppercase text-muted-foreground">
              Operation
            </span>
            <Badge
              variant="secondary"
              className={cn(
                "shrink-0 border-0 text-[11px] font-medium",
                getBadgeClassName(item.badgeTone)
              )}
            >
              {item.badgeLabel}
            </Badge>
            <p
              className="min-w-0 flex-1 break-words text-sm font-medium text-foreground [unicode-bidi:plaintext]"
              dir="ltr"
              title={item.title}
            >
              <AssistantLinkedText parts={item.titleParts} fallback={item.title} />
            </p>
          </div>
          <p
            className="break-words whitespace-pre-wrap text-xs text-muted-foreground [unicode-bidi:plaintext]"
            dir="ltr"
            title={item.description}
          >
            <AssistantLinkedText parts={item.descriptionParts} fallback={item.description} />
          </p>
          {item.toolCallSummaries && item.toolCallSummaries.length > 1 ? (
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              {item.toolCallSummaries.map((summary, index) => (
                <li
                  key={summary}
                  className="break-words [unicode-bidi:plaintext]"
                  dir="ltr"
                  title={summary}
                >
                  <AssistantLinkedText
                    parts={item.toolCallSummaryParts?.[index]}
                    fallback={summary}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className={cn("flex items-stretch gap-2", compact ? "w-full" : "flex-col")}>
          <Button
            type="button"
            size="sm"
            className={cn("min-w-24", compact && "flex-1")}
            onClick={() => void handleApprove()}
            disabled={inFlightAction !== null || !representativeToolCallId}
          >
            {inFlightAction === "approve"
              ? `${item.primaryActionLabel}...`
              : item.primaryActionLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn("min-w-24 border-border/80 text-muted-foreground", compact && "flex-1")}
            onClick={() => void handleDeny()}
            disabled={inFlightAction !== null || !representativeToolCallId}
          >
            {inFlightAction === "deny"
              ? `${item.secondaryActionLabel}...`
              : item.secondaryActionLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn("min-w-24 text-muted-foreground", compact && "flex-1")}
            onClick={() => void handleAbort()}
            disabled={inFlightAction !== null}
          >
            {inFlightAction === "abort" ? "Aborting..." : "Abort"}
          </Button>
        </div>
      </div>
    </section>
  );
}
